import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { auth as appClientAuth } from '@edf-pkg/app-client';
import { store as appMainStore } from '@edf-pkg/app-main';
import { getStore } from '@edf-pkg/app-store';

import { transformToCamelCase } from '$app-web/utils';

import { fetchAuthenticationTokens } from '../../endpoints';

const PUBLIC_ENDPOINTS = Object.freeze([
    'v1/auth/token',
    'v1/auth/reset_password',
    'v1/auth/set_password',
    'v1/auth/2fa',
    'v1/eden/participation/confirmation',
    'v1/user/email',
    'v1/user/phone',
]);

function isPublicEndpoint(endpoint = ''): boolean {
    const endpointWithTrimmedSlashes = endpoint.replace(/^\/|\/$/g, '');
    return PUBLIC_ENDPOINTS.includes(endpointWithTrimmedSlashes);
}

function getNewAxiosConfig(prevConfig: AxiosRequestConfig, newToken: string): AxiosRequestConfig {
    const newConfig = {
        ...prevConfig,
        headers: {
            ...prevConfig.headers,
            Authorization: `Bearer ${newToken}`,
        },
    };

    return newConfig;
}

class TokenRefreshQueue {
    private isRefreshing = false;
    private queue: Array<{
        resolve: (token: string) => void;
        reject: (error: Error) => void;
    }> = [];

    public get isRefreshingToken(): boolean {
        return this.isRefreshing;
    }

    public startRefreshing(): void {
        this.isRefreshing = true;
    }

    public stopRefreshing(): void {
        this.isRefreshing = false;
    }

    public enqueue(resolve: (token: string) => void, reject: (error: Error) => void): void {
        this.queue.push({ resolve, reject });
    }

    public resolveQueue(token: string): void {
        this.queue.forEach((prom) => prom.resolve(token));
        this.queue = [];
    }

    public rejectQueue(error: Error): void {
        this.queue.forEach((prom) => prom.reject(error));
        this.queue = [];
    }
}

export function addTokenRefreshInterceptor(axios: AxiosInstance): void {
    const tokenQueue = new TokenRefreshQueue();

    axios.interceptors.response.use(
        (response: AxiosResponse) => response,
        async (error: AxiosError) => {
            if (!error.response || !error.config) {
                return Promise.reject(error);
            }

            const { config, response } = error;
            const { url = '' } = config;
            const { status } = response;
            const store = getStore();
            const { refreshToken } = appClientAuth.useAuthenticationStore.getState();

            if (status !== 401 || isPublicEndpoint(url) || !refreshToken) {
                return Promise.reject(error);
            }

            if (tokenQueue.isRefreshingToken) {
                return new Promise<AxiosResponse>((resolve, reject) => {
                    tokenQueue.enqueue((token) => {
                        const newConfig = getNewAxiosConfig(config, token);

                        resolve(axios(newConfig));
                    }, reject);
                });
            }

            tokenQueue.startRefreshing();

            try {
                const data = await fetchAuthenticationTokens(refreshToken);
                const { accessToken } = data;

                appClientAuth.useAuthenticationStore.getState().updateState(transformToCamelCase(data));

                store.dispatch(
                    appMainStore.userDuck.duckActionCreators.setUserDataExternal({
                        userData: { apiKey: accessToken },
                        actionSource: '',
                        shouldPersistUserData: true,
                        shouldLoadFromAPI: false,
                        shouldSetInitializationStatus: false,
                    })
                );

                tokenQueue.resolveQueue(accessToken);
                tokenQueue.stopRefreshing();

                const newConfig = getNewAxiosConfig(config, accessToken);

                return axios(newConfig);
            } catch (refreshError) {
                const typedError = refreshError as AxiosError;

                if (typedError.response?.status === 401) {
                    appClientAuth.useAuthenticationStore.getState().resetState();
                    store.dispatch(appMainStore.userDuck.duckActionCreators.logout());
                }

                tokenQueue.rejectQueue(new Error('Failed to refresh authentication token'));
                tokenQueue.stopRefreshing();

                return Promise.reject(typedError);
            }
        }
    );
}
